import { X } from "lucide-react";
import { useEffect, useState } from "react";

type BannerProps = {
  title: string
  description: string
  image?: string | React.ReactNode
  imageAlt?: string
  gradient?: {
    from: string
    to: string
  }
  backgroundColor?: string
  textColor?: string
  actionText?: string
  actionUrl?: string
  actionOnClick?: () => void
  actionTextColor?: string
  useLocalStorage?: boolean
  storageKey?: string
  showDuration?: number
  onClose?: () => void
  position?: "top" | "bottom"
  animationDuration?: number
  showCloseButton?: boolean
  showOverlay?: boolean
};

const DEFAULT_STORAGE_KEY = "banner_closed";
const DEFAULT_SHOW_DURATION = 1000 * 60 * 60 * 24 * 30;
const DEFAULT_ANIMATION_DURATION = 500;

// Helper functions to reduce complexity
const shouldShowBanner = (useLocalStorage: boolean, storageKey: string, showDuration: number): boolean => {
  if (!useLocalStorage) return true;

  const closedAt = localStorage.getItem(storageKey);
  return !closedAt || Date.now() - Number(closedAt) > showDuration;
};

const getBackgroundStyle = (gradient?: { from: string, to: string }, backgroundColor?: string) => {
  return gradient
    ? { background: `linear-gradient(to right, ${gradient.from}, ${gradient.to})` }
    : { backgroundColor };
};

const getPositionClasses = (position: "top" | "bottom") => {
  return position === "top" ? "top-0" : "bottom-0";
};

const getTransformClasses = (position: "top" | "bottom", entered: boolean) => {
  const baseTransform = entered
    ? "translate-y-0"
    : position === "top" ? "-translate-y-full" : "translate-y-full";
  return baseTransform;
};

const renderImage = (image: string | React.ReactNode, imageAlt: string) => {
  if (typeof image === "string") {
    return (
      <img
        src={image}
        alt={imageAlt}
        className="h-20 w-20 md:h-24 rounded-full md:w-24 flex-shrink-0"
      />
    );
  }
  return image;
};

const renderActionButton = (
  actionText: string,
  actionUrl?: string,
  actionOnClick?: () => void,
  actionTextColor: string = "#4f46e5",
) => {
  const buttonClasses = "px-6 py-2 rounded-full font-semibold bg-white shadow-lg transform transition animate-heartbeat hover:scale-105 hover:shadow-xl";
  const style = { color: actionTextColor };

  if (actionUrl) {
    return (
      <a
        href={actionUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={buttonClasses}
        style={style}
      >
        {actionText}
      </a>
    );
  }

  return (
    <button
      onClick={actionOnClick}
      className={`${buttonClasses} cursor-pointer`}
      style={style}
    >
      {actionText}
    </button>
  );
};

const CloseButton = ({ onClick }: { onClick: () => void }) => (
  <button
    onClick={onClick}
    aria-label="Fermer le bandeau"
    className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/20 transition-colors cursor-pointer"
  >
    <X size={20} />
  </button>
);

// Custom hook to manage banner visibility and timing
const useBannerVisibility = (
  useLocalStorage: boolean,
  storageKey: string,
  showDuration: number,
  animationDuration: number,
  onClose?: () => void,
) => {
  const [visible, setVisible] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    const shouldShow = shouldShowBanner(useLocalStorage, storageKey, showDuration);

    if (shouldShow) {
      setVisible(true);
      requestAnimationFrame(() => setEntered(true));
    }
  }, [useLocalStorage, storageKey, showDuration]);

  const handleClose = () => {
    setEntered(false);
    setTimeout(() => {
      setVisible(false);
      if (useLocalStorage) {
        localStorage.setItem(storageKey, Date.now().toString());
      }
      onClose?.();
    }, animationDuration);
  };

  return { visible, entered, handleClose };
};

const BannerContent = ({
  title,
  description,
  image,
  imageAlt,
  actionText,
  actionUrl,
  actionOnClick,
  actionTextColor,
}: {
  title: string
  description: string
  image?: string | React.ReactNode
  imageAlt: string
  actionText?: string
  actionUrl?: string
  actionOnClick?: () => void
  actionTextColor: string
}) => {
  const hasAction = actionText && (actionUrl || actionOnClick);

  return (
    <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-5 px-2 py-4">
      {image && renderImage(image, imageAlt)}

      <div
        className={`flex-1 text-center md:text-left space-y-1 ${
          !image ? "md:ml-0" : ""
        }`}
      >
        <h2 className="text-xl md:text-2xl font-bold">{title}</h2>
        <p className="text-sm md:text-base opacity-90">{description}</p>
      </div>

      {hasAction && renderActionButton(actionText, actionUrl, actionOnClick, actionTextColor)}
    </div>
  );
};

// Component for rendering the banner wrapper with overlay and animations
const BannerWrapper = ({
  showOverlay,
  position,
  animationDuration,
  entered,
  backgroundStyle,
  textColor,
  showCloseButton,
  handleClose,
  children,
}: {
  showOverlay: boolean
  position: "top" | "bottom"
  animationDuration: number
  entered: boolean
  backgroundStyle: React.CSSProperties
  textColor: string
  showCloseButton: boolean
  handleClose: () => void
  children: React.ReactNode
}) => {
  const positionClasses = getPositionClasses(position);
  const transformClasses = getTransformClasses(position, entered);

  return (
    <>
      {showOverlay && (
        <div className="fixed inset-0 bg-black opacity-30 w-full h-full z-[9998]" />
      )}
      <div
        className={`fixed inset-x-0 ${positionClasses} z-[9999] transform transition-all duration-${animationDuration} ease-out ${
          entered
            ? `${transformClasses} opacity-100`
            : `${transformClasses} opacity-0`
        }`}
      >
        <div
          className="relative text-white shadow-lg"
          style={{
            ...backgroundStyle,
            color: textColor,
          }}
        >
          {showCloseButton && <CloseButton onClick={handleClose} />}
          {children}
        </div>
      </div>
    </>
  );
};

export const Banner = ({
  title,
  description,
  image,
  imageAlt = "Banner image",
  gradient,
  backgroundColor = "#3b82f6",
  textColor = "white",
  actionText,
  actionUrl,
  actionOnClick,
  actionTextColor = "#4f46e5",
  useLocalStorage = false,
  storageKey = DEFAULT_STORAGE_KEY,
  showDuration = DEFAULT_SHOW_DURATION,
  onClose,
  position = "top",
  animationDuration = DEFAULT_ANIMATION_DURATION,
  showCloseButton = true,
  showOverlay = false,
}: BannerProps) => {
  const { visible, entered, handleClose } = useBannerVisibility(
    useLocalStorage,
    storageKey,
    showDuration,
    animationDuration,
    onClose,
  );

  if (!visible) return null;

  const backgroundStyle = getBackgroundStyle(gradient, backgroundColor);

  return (
    <BannerWrapper
      showOverlay={showOverlay}
      position={position}
      animationDuration={animationDuration}
      entered={entered}
      backgroundStyle={backgroundStyle}
      textColor={textColor}
      showCloseButton={showCloseButton}
      handleClose={handleClose}
    >
      <BannerContent
        title={title}
        description={description}
        image={image}
        imageAlt={imageAlt}
        actionText={actionText}
        actionUrl={actionUrl}
        actionOnClick={actionOnClick}
        actionTextColor={actionTextColor}
      />
    </BannerWrapper>
  );
};
